import {
  HostBinding,
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  forwardRef,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Navigation } from "../../../model/navigation";
import { SingleChoice } from "../../../model/singlechoice";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, Observer, combineLatest } from "rxjs";
import { map } from "rxjs/operators";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { TicketService } from "../../../services/ticket.service";
import { NavigationHelper } from "../../../services/navigation.helper";
import { Labeler } from "../../../model/labeler";
import { badgeFor } from "../../../model/badge";

@Component({
  selector: "single-choice",
  templateUrl: "./singlechoice.component.html",
  styleUrls: ["./singlechoice.component.css"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SingleChoiceComponent),
      multi: true,
    },
  ],
})
export class SingleChoiceComponent implements OnInit, ControlValueAccessor {
  @Output() choiceEvent: EventEmitter<SingleChoice> = new EventEmitter();
  @Output() choice: EventEmitter<number> = new EventEmitter();
  @Input() topic: string;
  @Input() label: string;
  @Input() editMode: boolean;
  @Input() ticketId: number | null;
  @Input() currentDepartmentId: number | null;
  @Input() completedUrl: Observable<string>;
  @Input() icon!: string;
  @Input() choiceObserver: Partial<Observer<number>>;
  @Input() labeler: Observable<string>;
  @Input() labelOnDemand: Labeler;
  /**
   * Shows the value but does not open the chooser. Not called 'disabled':
   * next to formControlName that name belongs to the forms directive, which
   * only warns about it.
   */
  @Input() locked: boolean = false;
  currentbackgroundColor = new BehaviorSubject<string>("#ccc");
  currentcolor = new BehaviorSubject<string>("#555");

  /**
   * The topics whose items each carry a colour of their own, chosen on the
   * status or priority screen. Everything else has one colour for the whole
   * category - a user is dark blue, a location is green - because a user has no
   * colour to be.
   */
  private static readonly OWN_COLOUR = ["status", "priority"];

  /**
   * The colour of the circle.
   *
   * makeColor gives the item's own colour where it has one; makeBackgroundColor
   * gives the category's. The circle used to take the category colour always,
   * and the item's colour was put on the icon instead - so every status was the
   * same tomato red with a differently coloured tick in it, and because the
   * status icon is a filled disc, the two read as a fat ring around a dot
   * rather than as one coloured circle.
   */
  readonly circleBackground: Observable<string> = combineLatest([
    this.currentcolor,
    this.currentbackgroundColor,
  ]).pipe(
    map(([own, category]) =>
      this.carriesItsOwnColour && own && own.trim().length > 0 ? own : category
    )
  );

  get carriesItsOwnColour(): boolean {
    return SingleChoiceComponent.OWN_COLOUR.includes(this.topic);
  }

  /**
   * The circle's colour as a css custom property, so the stylesheet can mix
   * lighter and darker shades of it - the border, the fill and the text are all
   * the same colour at different strengths. The list pill does exactly this,
   * which is what makes the two read as the same object at two sizes.
   */
  @HostBinding("style.--choice-color") choiceColor = "#cccccc";

  /**
   * Departments are the exception the stylesheet needs to know about: their
   * colour is picked by hand rather than mixed, so the pill can match the chip
   * in the list exactly instead of coming close.
   */
  @HostBinding("class.topic-departments") get isDepartments(): boolean {
    return this.topic === "departments";
  }
  selectedId: number;
  currentLabel = new BehaviorSubject<string>("");
  selectedCompletedUrl: string;
  selectedChoice: SingleChoice;
  status: number = 0;
  disabled: boolean = false;
  selecteLabel: string = "";
  /** The chosen value's importance, for a priority; null until it is known. */
  importance: number | null = null;
  constructor(
    private translate: TranslateService,
    private router: Router,
    private ticket: TicketService,
    private nav: NavigationHelper
  ) {}

  ngOnInit() {
    this.followTheColour();
    // the only chooser that keeps an icon of its own: it picks an action, not
    // a kind of thing
    if (!this.icon && this.topic === "multi-action") {
      this.icon = "low_priority";
    }
    if (this.labeler) {
      this.labeler.subscribe((label) => {
        this.currentLabel.next(label);
      });
    } else {
      this.currentLabel.next(this.label);
    }
    if (this.completedUrl) {
      this.completedUrl.subscribe((url) => (this.selectedCompletedUrl = url));
    } else {
      this.selectedCompletedUrl = "";
    }

    this.labelOnDemand.makeColor(this.selectedId as number).subscribe((c) => {
      if (c && c.trim().length > 0) {
        this.currentcolor.next(c);
      }
    });
    this.labelOnDemand
      .makeBackgroundColor(this.selectedId as number)
      .subscribe((bc) => {
        if (bc && bc.trim().length > 0) {
          this.currentbackgroundColor.next(bc);
        }
      });
  }

  private followTheColour(): void {
    this.circleBackground.subscribe((colour) => {
      if (colour && colour.trim().length > 0) {
        this.choiceColor = colour;
      }
    });
  }

  onClickChoice() {
    if (this.locked) {
      return;
    }
    this.choiceObserver;

    const callback = this.choiceObserver;

    if (
      this.editMode &&
      this.ticketId &&
      (this.ticketId > 0 || this.ticketId === Navigation.NEW)
    ) {
      const safeTicketId = this.ticketId;

      this.status = 1;
      this.selectedChoice = new SingleChoice(this.topic);

      this.selectedChoice.setup().subscribe({
        next: (value) => {
          this.selectedId = value;
         
          this.currentLabel.next(this.selectedChoice.label);
          this.currentbackgroundColor.next(this.selectedChoice.backgroundColor);
          this.currentcolor.next(this.selectedChoice.color);
          this.followImportance(value);

          this.status = 2;

          this.onChange(this.selectedId);

          var url = this.selectedCompletedUrl;
          if (
            !this.selectedCompletedUrl ||
            this.selectedCompletedUrl.length === 0
          ) {
            url = `tickets/${safeTicketId}/edit`;
          }

          this.router.navigate([url]).then(() => {
            this.choice?.emit(this.selectedId);

            this.nav.freshNavigation;
            this.choiceObserver?.next?.(value);
          });
        },
      });

      this.choiceEvent.emit(this.selectedChoice);

      this.router
        .navigate([`tickets/${this.ticketId}/choose/${this.topic}`])
        .then(() => {
          this.status = 2;
          this.nav.freshNavigation;
        });
    } else {
    }
  }

  /** Whether a priority is the high one is in its importance, not in its name. */
  private followImportance(id: number): void {
    this.importance = null;
    if (this.topic === "priority" && this.labelOnDemand) {
      this.labelOnDemand
        .makeImportance(id)
        .subscribe((importance) => (this.importance = importance));
    }
  }

  /**
   * The icon in the pill, or null.
   *
   * The multi-action chooser keeps its own. Everywhere else the colour says
   * what kind of thing the value is, and only a done status and a high
   * priority still carry an icon (#171).
   */
  get shownIcon(): string | null {
    if (this.topic === "multi-action") {
      return this.icon || null;
    }
    return badgeFor(this.topic, this.currentLabel.value, this.importance);
  }

  onChange = (id: number) => {};

  onTouched = () => {};

  writeValue(id: number): void {
    this.selectedId = id;
    this.followImportance(id);

    if (this.labelOnDemand) {
      this.labelOnDemand.makeLabel(id).subscribe((l) => {
        if (l && l.trim().length > 0) {
          this.currentLabel.next(l);
        }
      });

      this.labelOnDemand.makeColor(id).subscribe((c) => {
        if (c && c.trim().length > 0) {
          this.currentcolor.next(c);
        }
      });

      this.labelOnDemand.makeBackgroundColor(id).subscribe((bc) => {
        if (bc && bc.trim().length > 0) {
          this.currentbackgroundColor.next(bc);
        }
      });
    }
  }

  registerOnChange(fn: (id: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  get selectedLabel(): Observable<string> {
    return this.currentLabel;
  }
}
